<template>
  <b-modal
    :visible="visible"
    size="lg"
    centered
    scrollable
    hide-footer
    dialog-class="avt-dialog"
    @change="$emit('update:visible', $event)"
  >
    <template #modal-title>
      <span class="avt-title">
        <feather-icon
          icon="UsersIcon"
          size="18"
          class="mr-50"
        />
        {{ $t('technicalDecision.teamRequest.teams.title') }}
      </span>
      <small class="avt-subtitle">
        {{ $t('technicalDecision.teamRequest.teams.subtitle', { available: totals.available, total: totals.total }) }}
      </small>
    </template>

    <!-- Filtro por tipo + mostrar indisponíveis -->
    <div class="avt-toolbar">
      <div class="avt-pills">
        <button
          type="button"
          class="avt-pill"
          :class="{ 'avt-pill--on': filter === null }"
          @click="filter = null"
        >
          {{ $t('technicalDecision.teamRequest.teams.all') }}
          <span class="avt-pill__count">{{ totals.available }}</span>
        </button>
        <button
          v-for="type in typesWithTeams"
          :key="type.id"
          type="button"
          class="avt-pill"
          :class="{ 'avt-pill--on': filter === type.id, 'avt-pill--empty': type.available === 0 }"
          @click="filter = type.id"
        >
          {{ type.name }}
          <span class="avt-pill__count">{{ type.available }}</span>
        </button>
      </div>
      <b-form-checkbox
        v-if="hasUnavailable"
        v-model="showUnavailable"
        switch
        size="sm"
      >
        {{ $t('technicalDecision.teamRequest.teams.showUnavailable') }}
      </b-form-checkbox>
    </div>

    <div
      v-if="!groups.length"
      class="avt-empty"
    >
      <feather-icon
        icon="UserXIcon"
        size="34"
        class="avt-empty__icon"
      />
      <!-- Nenhuma equipe entrou na conta: o motivo está no cadastro, não na disponibilidade. -->
      <template v-if="!totals.total && excluded.count">
        <strong>{{ $t('technicalDecision.teamRequest.teams.emptyDiscipline') }}</strong>
        <span>{{ $t('technicalDecision.teamRequest.teams.emptyDisciplineHint', { count: excluded.count, names: excluded.names.join(', ') }) }}</span>
      </template>
      <template v-else-if="!totals.total">
        <strong>{{ $t('technicalDecision.teamRequest.teams.emptyNone') }}</strong>
        <span>{{ $t('technicalDecision.teamRequest.teams.emptyNoneHint') }}</span>
      </template>
      <template v-else>
        <strong>{{ $t('technicalDecision.teamRequest.teams.empty') }}</strong>
        <span>{{ $t('technicalDecision.teamRequest.teams.emptyHint') }}</span>
      </template>
    </div>

    <section
      v-for="group in groups"
      :key="group.id"
      class="avt-group"
    >
      <header class="avt-group__head">
        <h6 class="mb-0">
          {{ group.name }}
          <span class="avt-group__count">
            {{ $t('technicalDecision.teamRequest.teams.subtitle', { available: group.available, total: group.total }) }}
          </span>
        </h6>
        <button
          type="button"
          class="btn btn-sm"
          :class="selectedTypeIds.includes(group.id) ? 'btn-primary' : 'btn-outline-primary'"
          @click="$emit('toggle-type', group.id)"
        >
          <feather-icon
            :icon="selectedTypeIds.includes(group.id) ? 'CheckIcon' : 'PlusIcon'"
            size="13"
            class="mr-25"
          />
          {{ selectedTypeIds.includes(group.id)
            ? $t('technicalDecision.teamRequest.teams.typeSelected')
            : $t('technicalDecision.teamRequest.teams.useType') }}
        </button>
      </header>

      <div class="avt-grid">
        <article
          v-for="team in group.teams"
          :key="team.id"
          class="avt-card"
          :class="{ 'avt-card--off': !team.available }"
        >
          <div class="avt-card__top">
            <strong class="avt-card__name">{{ team.name }}</strong>
            <span
              class="avt-status"
              :class="team.available ? 'avt-status--ok' : 'avt-status--off'"
            >
              {{ statusLabel(team) }}
            </span>
          </div>

          <div
            v-if="team.vehicle"
            class="avt-line"
          >
            <feather-icon
              icon="TruckIcon"
              size="12"
              class="mr-50"
            />{{ team.vehicle }}
          </div>

          <div
            class="avt-line"
            :title="team.crew.join(', ')"
          >
            <feather-icon
              icon="UsersIcon"
              size="12"
              class="mr-50"
            />
            <template v-if="team.crew.length">
              {{ team.crew.length }} · {{ crewPreview(team.crew) }}
            </template>
            <span
              v-else
              class="text-muted"
            >{{ $t('technicalDecision.teamRequest.teams.noCrew') }}</span>
          </div>

          <div
            v-if="team.position"
            class="avt-line"
          >
            <feather-icon
              icon="MapPinIcon"
              size="12"
              class="mr-50"
            />
            <span class="avt-position">{{ team.position.code }}</span>
            {{ team.position.name }}
            <small
              v-if="team.position_at"
              class="text-muted ml-25"
            >· {{ $t('technicalDecision.teamRequest.teams.since', { time: ago(team.position_at) }) }}</small>
          </div>
        </article>
      </div>
    </section>
  </b-modal>
</template>

<script>
import { BModal, BFormCheckbox } from 'bootstrap-vue'
import { serverNow } from '@/libs/serverTime'

/**
 * Quais equipes estão (ou não) livres para uma solicitação de equipe: o cartão do tipo só
 * mostra "2 de 3 livres", aqui o operador vê quem são, a tripulação, onde estão e por que
 * as demais não podem ser despachadas.
 */
export default {
  name: 'AvailableTeamsModal',
  components: { BModal, BFormCheckbox },
  props: {
    visible: { type: Boolean, default: false },
    // [{ id, name, total, available, teams: [...] }] vindos de team_types/availability
    teamTypes: { type: Array, default: () => [] },
    // Tipos já escolhidos na solicitação (para marcar o botão "Usar este tipo").
    selectedTypeIds: { type: Array, default: () => [] },
    // `meta.excluded_by_discipline` da disponibilidade: equipes do nó que ficaram de fora.
    excluded: { type: Object, default: () => ({ count: 0, names: [] }) },
  },
  data() {
    return { filter: null, showUnavailable: false }
  },
  computed: {
    typesWithTeams() {
      return this.teamTypes.filter(t => t.total > 0)
    },
    totals() {
      return this.typesWithTeams.reduce(
        (sum, t) => ({ available: sum.available + t.available, total: sum.total + t.total }),
        { available: 0, total: 0 },
      )
    },
    hasUnavailable() {
      return this.totals.total > this.totals.available
    },
    // Um bloco por tipo, só com as equipes que o filtro deixa ver.
    groups() {
      return this.typesWithTeams
        .filter(t => this.filter === null || t.id === this.filter)
        .map(t => ({ ...t, teams: t.teams.filter(team => team.available || this.showUnavailable) }))
        .filter(t => t.teams.length)
    },
  },
  watch: {
    // Sem nenhuma livre, já mostra as indisponíveis: é o que o operador quer entender.
    visible(open) {
      if (open) {
        this.filter = null
        this.showUnavailable = this.totals.available === 0 && this.hasUnavailable
      }
    },
  },
  methods: {
    statusLabel(team) {
      if (team.available) return this.$t('technicalDecision.teamRequest.teams.status.available')
      if (team.reason === 'in_dispatch') {
        return this.$t('technicalDecision.teamRequest.teams.status.in_dispatch', { protocol: team.occurrence_protocol || '—' })
      }

      return this.$t(`technicalDecision.teamRequest.teams.status.${team.reason}`)
    },

    crewPreview(crew) {
      const shown = crew.slice(0, 2).join(', ')

      return crew.length > 2 ? `${shown} +${crew.length - 2}` : shown
    },

    // "há X": minutos, horas ou dias, o que for mais legível.
    ago(iso) {
      const seconds = Math.max(0, Math.floor((serverNow() - Date.parse(iso)) / 1000))
      if (seconds < 60) return this.$t('technicalDecision.teamRequest.teams.now')
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes} min`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours} h`

      return `${Math.floor(hours / 24)} d`
    },
  },
}
</script>

<style scoped>
.avt-title {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
}

.avt-subtitle {
  margin-left: 10px;
  color: #82868b;
}

.avt-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.avt-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.avt-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid #d8d6de;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  cursor: pointer;
}

.avt-pill--on {
  border-color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  font-weight: 600;
}

.avt-pill--empty:not(.avt-pill--on) {
  opacity: 0.6;
}

.avt-pill__count {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(130, 134, 139, 0.18);
  font-size: 11px;
  text-align: center;
}

.avt-group + .avt-group {
  margin-top: 18px;
}

.avt-group__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.avt-group__count {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #82868b;
}

.avt-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 10px;
}

.avt-card {
  padding: 10px 12px;
  border: 1px solid #ebe9f1;
  border-left: 4px solid #28c76f;
  border-radius: 6px;
  background: rgba(40, 199, 111, 0.05);
}

.avt-card--off {
  border-left-color: #b9b9c3;
  background: rgba(130, 134, 139, 0.06);
}

.avt-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.avt-card__name {
  font-size: 15px;
}

.avt-status {
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.avt-status--ok {
  background: rgba(40, 199, 111, 0.16);
  color: #1f9d57;
}

.avt-status--off {
  background: rgba(130, 134, 139, 0.18);
  color: #6e6b7b;
}

.avt-line {
  display: flex;
  align-items: center;
  margin-top: 3px;
  font-size: 12.5px;
}

.avt-position {
  margin-right: 5px;
  padding: 0 6px;
  border-radius: 4px;
  background: rgba(115, 103, 240, 0.14);
  color: #7367f0;
  font-weight: 700;
}

.avt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 30px 10px;
  color: #82868b;
  text-align: center;
}

.avt-empty__icon {
  margin-bottom: 6px;
  opacity: 0.6;
}

.dark-layout .avt-card {
  border-color: #3b4253;
}

.dark-layout .avt-pill {
  border-color: #3b4253;
}
</style>
